import * as React from "react";
import { Plus, Trash2 } from "lucide-react";
import { SwapVert } from "@mui/icons-material";

import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

import type {
  SortField,
  SortValue,
  SortableAttribute,
  DynamicSort,
} from "../types";
import { SortRow, DynamicSortRow } from ".";

export interface SortButtonProps {
  // Legacy sort props (static fields)
  sortFields?: SortField[];
  sorts?: SortValue[];
  onSortsChange?: (sorts: SortValue[]) => void;
  // Dynamic sort props (attribute-based)
  sortableAttributes?: SortableAttribute[];
  dynamicSorts?: DynamicSort[];
  onAddSort?: (attribute: SortableAttribute) => void;
  onUpdateSort?: (index: number, updates: Partial<DynamicSort>) => void;
  onRemoveSort?: (index: number) => void;
  onClearSorts?: () => void;
  // Optional: allow overriding classnames
  className?: string;
}

export function SortButton({
  sortFields = [],
  sorts = [],
  onSortsChange,
  sortableAttributes = [],
  dynamicSorts = [],
  onAddSort,
  onUpdateSort,
  onRemoveSort,
  onClearSorts,
  className,
}: SortButtonProps) {
  const [open, setOpen] = React.useState(false);

  const useDynamic = sortableAttributes.length > 0 && !!onAddSort;

  const activeCount = useDynamic
    ? dynamicSorts.filter((s) => s.field).length
    : sorts.filter((s) => s.field).length;

  const usedFields = sorts.map((s) => s.field).filter(Boolean);
  const canAddMoreLegacy = usedFields.length < sortFields.length;
  const availableAttributes = sortableAttributes.filter(
    (attr) => !dynamicSorts.some((s) => s.field === attr.field)
  );
  const getAttributeByField = (field: string) =>
    sortableAttributes.find((attr) => attr.field === field);

  const handleAddSort = () => {
    onSortsChange?.([...sorts, { field: "", direction: "asc" }]);
  };
  const handleUpdateSort = (index: number, sort: SortValue) => {
    const newSorts = [...sorts];
    newSorts[index] = sort;
    onSortsChange?.(newSorts);
  };
  const handleRemoveSort = (index: number) => {
    const newSorts = sorts.filter((_, i) => i !== index);
    onSortsChange?.(newSorts);
  };

  const showButton = useDynamic
    ? sortableAttributes.length > 0
    : sortFields.length > 0;
  if (!showButton) return null;

  return (
    <Popover open={open} onOpenChange={setOpen} modal={true}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          intent="primary"
          design="outline"
          size="iconMd"
          className={cn(
            "aero:h-10 aero:rounded aero:shrink-0 aero:mt-7 aero:relative",
            "aero:hover:bg-brand-primary-50 ",
            activeCount > 0
              ? "aero:w-auto aero:px-3 aero:gap-2 aero:bg-brand-primary-50 aero:border-brand-primary-300"
              : "aero:w-11",
            className
          )}
        >
          <SwapVert className="aero:h-5 aero:w-5" />
          {activeCount > 0 && (
            <span className="aero:flex aero:items-center aero:justify-center aero:h-5 aero:w-5 aero:rounded-full aero:bg-brand-primary-700 aero:text-white aero:text-xs aero:font-bold">
              {activeCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="aero:w-[450px] aero:p-5 aero:rounded-lg aero:shadow-[0px_4px_4px_0px_rgba(127,127,127,0.1)]"
        sideOffset={8}
      >
        <div className="aero:flex aero:flex-col aero:gap-3">
          <div className="aero:flex aero:items-center aero:justify-between">
            <h4 className="aero:text-sm aero:font-bold aero:text-foreground">
              Sort by
            </h4>
            <div className="aero:flex aero:items-center aero:gap-2">
              {useDynamic ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      type="button"
                      intent="secondary"
                      design="outline"
                      size="sm"
                      disabled={availableAttributes.length === 0}
                    >
                      <Plus className="aero:h-4 aero:w-4" />
                      Add Sort
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    className="aero:max-h-64 aero:overflow-y-auto"
                  >
                    {availableAttributes.map((attr) => (
                      <DropdownMenuItem
                        key={attr.id}
                        onClick={() => onAddSort?.(attr)}
                      >
                        {attr.label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Button
                  type="button"
                  intent="secondary"
                  design="outline"
                  size="sm"
                  onClick={handleAddSort}
                  disabled={!canAddMoreLegacy}
                >
                  <Plus className="aero:h-4 aero:w-4" />
                  Add Sort
                </Button>
              )}
            </div>
          </div>

          {useDynamic ? (
            dynamicSorts.length === 0 ? (
              <p className="aero:text-sm aero:text-neutral-500 aero:text-center aero:py-4">
                No sorts added. Click "Add Sort" to add one.
              </p>
            ) : (
              <>
                <div className="aero:flex aero:flex-col aero:gap-2">
                  {dynamicSorts.map((sort, index) => (
                    <DynamicSortRow
                      key={`${sort.field}-${index}`}
                      sort={sort}
                      attribute={getAttributeByField(sort.field)}
                      availableAttributes={availableAttributes}
                      onUpdate={(updates) => onUpdateSort?.(index, updates)}
                      onRemove={() => onRemoveSort?.(index)}
                      onChangeAttribute={(newAttr) => {
                        onUpdateSort?.(index, {
                          field: newAttr.field,
                          nestedField: newAttr.nestedField,
                        });
                      }}
                    />
                  ))}
                </div>
                {onClearSorts && (
                  <div className="aero:flex aero:items-start">
                    <Button
                      type="button"
                      intent="destructive"
                      design="text"
                      size="sm"
                      onClick={onClearSorts}
                    >
                      <Trash2 className="aero:h-4 aero:w-4" />
                      Clear all
                    </Button>
                  </div>
                )}
              </>
            )
          ) : sorts.length === 0 ? (
            <p className="aero:text-sm aero:text-neutral-500 aero:text-center aero:py-4">
              No sorts added. Click "Add Sort" to add one.
            </p>
          ) : (
            <div className="aero:flex aero:flex-col aero:gap-2">
              {sorts.map((sort, index) => (
                <SortRow
                  key={index}
                  sort={sort}
                  fields={sortFields}
                  usedFields={usedFields}
                  onUpdate={(s) => handleUpdateSort(index, s)}
                  onRemove={() => handleRemoveSort(index)}
                />
              ))}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
